import { action, makeAutoObservable, observable } from "mobx";

import {
  IMessageStore,
  MessageStore,
} from "@/components/Playground/Messages/Message/message-store";
import type { Message, MessageType } from "@/components/Playground/types";
import type { MessageValidationError } from "@/components/Playground/Messages/PlaceholderContainer/errors/types";

// Child messages store for managing messages within the placeholder container
export class ChildMessagesStore {
  messages: IMessageStore[] = [];
  hydrationVersion = 0;

  // Validation error state
  @observable parseError: string | null = null;
  @observable validationErrors = new Map<string, MessageValidationError[]>();

  constructor() {
    makeAutoObservable(this);
  }

  @action
  addMessage() {
    const newMessage = new MessageStore({
      role: "user",
      content: [{ type: "text", text: "" }],
    });
    this.messages.push(newMessage);
  }

  @action
  deleteMessage(id: string) {
    const index = this.messages.findIndex(m => m.id === id);
    if (index !== -1) {
      this.messages.splice(index, 1);
    }
  }

  @action
  reorderMessages(sourceIndex: number, destinationIndex: number) {
    if (
      sourceIndex < 0 ||
      sourceIndex >= this.messages.length ||
      destinationIndex < 0 ||
      destinationIndex >= this.messages.length
    ) {
      return;
    }
    const [removed] = this.messages.splice(sourceIndex, 1);
    this.messages.splice(destinationIndex, 0, removed);
  }

  /**
   * Hydrates the store from an array of serialized messages.
   * Used for loading existing placeholder content from input variables.
   */
  @action
  hydrateFromMessages(messages: Message[]) {
    this.messages = messages.map(
      msg =>
        new MessageStore({
          id: msg.id,
          role: msg.role as MessageType,
          content: msg.content,
          tool_calls: msg.tool_calls,
          function_call: msg.function_call,
          tool_call_id: msg.tool_call_id,
          name: msg.name,
        }),
    );
    this.hydrationVersion += 1;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Validation error methods
  // ─────────────────────────────────────────────────────────────────────────────

  @action
  setParseError(error: string | null) {
    this.parseError = error;
  }

  @action
  setValidationErrors(errors: Map<string, MessageValidationError[]>) {
    this.validationErrors = errors;
  }

  @action
  clearAllErrors() {
    this.parseError = null;
    this.validationErrors.clear();
  }

  @action
  clearErrorForMessage(messageId: string) {
    this.validationErrors.delete(messageId);
  }

  /**
   * Gets validation errors for a specific message.
   */
  getErrorsForMessage(messageId: string): MessageValidationError[] {
    return this.validationErrors.get(messageId) ?? [];
  }

  /**
   * Checks if a message has validation errors.
   */
  hasErrorForMessage(messageId: string): boolean {
    return this.validationErrors.has(messageId);
  }

  /**
   * Checks if there are any validation errors (parse or structural).
   */
  get hasErrors(): boolean {
    return this.parseError !== null || this.validationErrors.size > 0;
  }
}
